/**
 * Utility functions for extracting images from Picsart canvas/editor.
 * Based on aistudiominiapp replayUtils.ts implementation.
 */

import type { IReplayLayer, IReplayImageLayer } from '@picsart/web-replay-sdk';
import { getContext } from '@/context';
import { withTimeout } from '@/utils/async';
import { resolveLocalUrl } from './localUrlResolver';

export interface CanvasImage {
  id: string;
  name: string;
  url: string;
  width?: number;
  height?: number;
}

// Type guard to check if layer is an image layer
const isImageLayer = (layer: IReplayLayer): layer is IReplayImageLayer => {
  if (layer.mediaType === 'PHOTO' || layer.mediaType === 'IMAGE') {
    return true;
  }
  if (layer.type === 'image') {
    return true;
  }
  // Check if layer has image props (duck typing for compatibility)
  const imageProps = (layer as IReplayImageLayer).props?.image;
  if (imageProps?.value?.location) {
    return true;
  }
  return false;
};

/**
 * Check if a layer is a system/container/background layer
 */
const isSystemLayer = (layer: IReplayLayer): boolean => {
  const systemMediaTypes = [
    'CANVAS_GROUP',
    'GROUP',
    'CANVAS',
    'ROOT',
    'PROJECT_ROOT',
    'BG',
    'BACKGROUND',
    'BACKGROUND_COLOR',
  ];
  const systemTypes = [
    'canvas',
    'group',
    'root',
    'container',
    'bg',
    'background',
  ];

  if (systemMediaTypes.includes(layer.mediaType)) {
    return true;
  }
  if (systemTypes.includes(layer.type.toLowerCase())) {
    return true;
  }

  const nameLower = layer.name.toLowerCase();
  const mediaTypeLower = layer.mediaType.toLowerCase();
  if (
    nameLower === 'bg' ||
    nameLower.includes('background') ||
    nameLower.startsWith('bg_')
  )
    return true;
  if (mediaTypeLower.includes('background')) return true;

  return false;
};

/**
 * Check if an image URL is a system/placeholder image
 */
const isSystemImage = (url: string): boolean => {
  const systemPatterns = [
    'placeholder',
    'blank',
    'empty',
    'transparent.png',
    'data:image/gif;base64,R0lGODlhAQAB',
  ];
  const lowerUrl = url.toLowerCase();
  return systemPatterns.some((pattern) => lowerUrl.includes(pattern));
};

// SDK layers are stored as Record<UUIDString, IReplayLayer>
type LayersRecord = Record<string, IReplayLayer>;

/**
 * Extract all image layers from the canvas/editor.
 * Uses the miniapps-sdk to access scene layers.
 */
export const extractCanvasImages = async (): Promise<CanvasImage[]> => {
  try {
    const context = getContext();

    // Early return if scene is not properly initialized or has no layers
    // This prevents errors when SDK's sync() iterates layers with undefined entries
    const layers = context?.scene?.replay?.context?.layers;
    if (!layers || Object.keys(layers).length === 0) {
      return [];
    }

    // Sync scene to get latest state (with 3s timeout)
    await withTimeout({
      promise: context.scene.sync(),
      timeoutMs: 3000,
      fallback: undefined,
    });

    const { replay } = context.scene;

    if (!replay?.context?.layers) {
      return [];
    }

    // SDK layers are stored as Record<UUIDString, IReplayLayer>
    const layersData = replay.context.layers as LayersRecord;
    // Filter out undefined entries that may exist in corrupted canvas state
    const layerEntries = Object.entries(layersData).filter(
      ([, layer]) => layer != null
    );

    // Filter and map to CanvasImage objects
    const images = layerEntries
      .filter(([, layer]) => {
        // Skip system layers
        if (isSystemLayer(layer)) return false;
        // Skip non-image layers
        if (!isImageLayer(layer)) return false;
        // Check for valid image URL
        const rawUrl = layer.props.image.value.location;
        if (!rawUrl) return false;
        // Skip placeholder/system images
        if (isSystemImage(rawUrl)) return false;
        return true;
      })
      .map(([layerId, layer], index) => {
        // Type narrowed by isImageLayer guard
        const imageLayer = layer as IReplayImageLayer;
        const rawUrl = imageLayer.props.image.value.location;
        const imageUrl = resolveLocalUrl(rawUrl);
        return {
          id: layerId,
          name: imageLayer.name || `Image ${index + 1}`,
          url: imageUrl,
          width: imageLayer.props.image.value.width,
          height: imageLayer.props.image.value.height,
        };
      });

    return images;
  } catch {
    return [];
  }
};

/**
 * Get single image if exactly one image layer and no other content.
 * Used for auto-import scenario.
 */
export const getSingleCanvasImage = async (): Promise<CanvasImage | null> => {
  try {
    const context = getContext();

    // Early return if scene is not properly initialized or has no layers
    // This prevents errors when SDK's sync() iterates layers with undefined entries
    const layers = context?.scene?.replay?.context?.layers;
    if (!layers || Object.keys(layers).length === 0) {
      return null;
    }

    // Sync to ensure replay data is populated (with timeout)
    try {
      await withTimeout({
        promise: context.scene.sync(),
        timeoutMs: 3000,
        fallback: undefined,
      });
    } catch {
      // Sync timed out or failed, continue anyway
    }

    const { replay } = context.scene;

    if (!replay?.context?.layers) {
      return null;
    }

    // SDK layers are stored as Record<UUIDString, IReplayLayer>
    const layersData = replay.context.layers as LayersRecord;
    // Filter out undefined entries that may exist in corrupted canvas state
    const layerEntries = Object.entries(layersData).filter(
      ([, layer]) => layer != null
    );

    let imageCount = 0;
    let otherContentCount = 0;
    let singleImage: CanvasImage | null = null;

    layerEntries.forEach(([layerId, layer]) => {
      if (isSystemLayer(layer)) return;

      if (isImageLayer(layer)) {
        const rawUrl = layer.props.image.value.location;
        if (rawUrl && !isSystemImage(rawUrl)) {
          imageCount += 1;
          const imageUrl = resolveLocalUrl(rawUrl);
          singleImage = {
            id: layerId,
            name: layer.name || 'Image',
            url: imageUrl,
            width: layer.props.image.value.width,
            height: layer.props.image.value.height,
          };
        }
      } else {
        otherContentCount += 1;
      }
    });

    // Only return if exactly 1 image and no other content
    if (imageCount === 1 && otherContentCount === 0) {
      return singleImage;
    }

    return null;
  } catch {
    return null;
  }
};
